// MenuPublicView.jsx
import React, { useState, useEffect } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUtensils, faTriangleExclamation, faLeaf, faChevronLeft, faChevronRight, faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import menuPublicApi from './menuPublicApi';
import './menu-public-view.css';

const AGE_BRACKETS = ['1-3 роки', '3-6 років', '6-10 років'];
const WEEKDAY_SHORT = ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

const todayStr = () => new Date().toISOString().slice(0, 10);
const addDays = (isoDate, n) => {
  const d = new Date(isoDate + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const sumMeal = (items, field) => Math.round(items.reduce((acc, d) => acc + (Number(d[field]) || 0), 0) * 10) / 10;

// kindergartenId: required. date: optional, defaults to today. defaultAgeGroup: optional,
// pre-selects a tab. showDatePicker: false hides the date strip for a pure "today" public view.
const MenuPublicView = ({ kindergartenId, date: dateProp, defaultAgeGroup, showDatePicker = true }) => {
  const [date, setDate] = useState(dateProp || todayStr());
  const [windowStart, setWindowStart] = useState(addDays(dateProp || todayStr(), -3));
  const [activeAgeGroup, setActiveAgeGroup] = useState(defaultAgeGroup || AGE_BRACKETS[1]);
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!kindergartenId) return;
    setLoading(true);
    setError(null);
    menuPublicApi.getDay(kindergartenId, date)
      .then(setMenu)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [kindergartenId, date]);

  if (!kindergartenId) return null;

  const visibleDays = Array.from({ length: 7 }, (_, i) => {
    const iso = addDays(windowStart, i);
    const d = new Date(iso + 'T00:00:00');
    return { iso, dayNum: d.getDate(), weekday: WEEKDAY_SHORT[d.getDay()], isToday: iso === todayStr() };
  });

  const jumpToDate = (iso) => {
    setDate(iso);
    setWindowStart(addDays(iso, -3));
  };

  const ageGroupData = menu && menu.ageGroups
    ? menu.ageGroups.find(ag => ag.ageGroup === activeAgeGroup)
    : null;

  const hasAnyMenu = menu && menu.ageGroups && menu.ageGroups.some(ag => (ag.meals || []).some(m => (m.items || []).length > 0));
  const visibleMeals = ageGroupData ? ageGroupData.meals.filter(m => m.items.length > 0) : [];
  const dayCalories = visibleMeals.reduce((acc, m) => acc + sumMeal(m.items, 'calories'), 0);

  return (
    <div className="menu-public-view">
      <div className="menu-public-hero">
        <h4 className="menu-public-title">Меню на день</h4>
        {dayCalories > 0 && (
          <div className="menu-public-hero-kcal">
            <FontAwesomeIcon icon={faLeaf} className="me-2" />
            ~{Math.round(dayCalories)} ккал
            <FontAwesomeIcon icon={faLeaf} className="ms-2" />
          </div>
        )}
        {showDatePicker && (
          <div className="menu-date-strip">
            <button className="menu-date-nav" onClick={() => setWindowStart(prev => addDays(prev, -7))} aria-label="Попередній тиждень">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <div className="menu-date-days">
              {visibleDays.map(d => (
                <button
                  key={d.iso}
                  className={`menu-date-day ${d.iso === date ? 'active' : ''} ${d.isToday ? 'today' : ''}`}
                  onClick={() => setDate(d.iso)}
                >
                  <span className="menu-date-weekday">{d.weekday}</span>
                  <span className="menu-date-num">{d.dayNum}</span>
                </button>
              ))}
            </div>

            <button className="menu-date-nav" onClick={() => setWindowStart(prev => addDays(prev, 7))} aria-label="Наступний тиждень">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>

            <label className="menu-date-jump" title="Обрати іншу дату">
              <FontAwesomeIcon icon={faCalendarDays} />
              <input type="date" value={date} onChange={(e) => jumpToDate(e.target.value)} />
            </label>
          </div>
        )}
      </div>

      <div className="menu-public-tabs">
        {AGE_BRACKETS.map(b => (
          <button
            key={b}
            className={`menu-public-tab ${activeAgeGroup === b ? 'active' : ''}`}
            onClick={() => setActiveAgeGroup(b)}
          >
            {b}
          </button>
        ))}
      </div>

      {error && <Alert variant="danger" className="m-3">{error}</Alert>}

      {loading ? (
        <div className="text-center py-4"><Spinner animation="border" /></div>
      ) : !hasAnyMenu ? (
        <p className="text-muted text-center py-4 mb-0">Меню на цей день ще не опубліковано.</p>
      ) : visibleMeals.length === 0 ? (
        <p className="text-muted text-center py-4 mb-0">Для цієї вікової групи меню на цей день не додано.</p>
      ) : (
        <div className="menu-public-meals">
          {visibleMeals.map(meal => {
            const heroPhoto = meal.items.find(d => d.photoUrl);
            const mealCalories = sumMeal(meal.items, 'calories');
            const mealProteins = sumMeal(meal.items, 'proteins');
            const mealFats = sumMeal(meal.items, 'fats');
            const mealCarbs = sumMeal(meal.items, 'carbohydrates');

            return (
              <div key={meal.mealType} className="menu-meal-card">
                <div className="menu-meal-photo">
                  {heroPhoto && heroPhoto.photoUrl ? (
                    <img src={menuPublicApi.photoSrc(heroPhoto.photoUrl)} alt={heroPhoto.name} />
                  ) : (
                    <div className="menu-meal-photo--placeholder">
                      <FontAwesomeIcon icon={faUtensils} size="2x" />
                    </div>
                  )}
                </div>

                <div className="menu-meal-info">
                  <div className="menu-meal-type">{meal.mealType}</div>
                  <ul className="menu-meal-dish-list">
                    {meal.items.map((dish, i) => (
                      <li key={i}>
                        <div className="menu-meal-dish-line">
                          <span className="menu-meal-dish-name">{dish.name}</span>
                          {dish.portionWeight > 0 && (
                            <span className="menu-meal-dish-weight">{dish.portionWeight} г</span>
                          )}
                          {dish.allergens && dish.allergens !== 'Немає' && (
                            <span className="menu-meal-allergen">
                              <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
                              {dish.allergens}
                            </span>
                          )}
                        </div>
                        {dish.publicDescription && (
                          <div className="menu-meal-dish-description">{dish.publicDescription}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                {mealCalories > 0 && (
                  <div className="menu-meal-nutrition">
                    <div className="menu-meal-kcal-circle">
                      <span className="menu-meal-kcal-value">{Math.round(mealCalories)}</span>
                      <span className="menu-meal-kcal-label">ккал</span>
                    </div>
                    <div className="menu-meal-macros">
                      <div>Б: <strong>{mealProteins} г</strong></div>
                      <div>Ж: <strong>{mealFats} г</strong></div>
                      <div>В: <strong>{mealCarbs} г</strong></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MenuPublicView;